// =============================================================================
// pages/MarketingHub.js — Pazarlama merkezi (Faz G)
// =============================================================================
// 3 modül tek sayfada: müşteri segmentleri, drip kampanya, sosyal medya scheduler
// =============================================================================

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Chip, Button, IconButton,
  Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailIcon from "@mui/icons-material/Email";
import ShareIcon from "@mui/icons-material/Share";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  getSegments, reclassifySegments, getDripCampaigns,
  getSocialPosts, createSocialPost, cancelSocialPost,
  getMarketingSummary
} from "../services/api";
import toast, { Toaster } from "react-hot-toast";

const PLATFORM_COLOR = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  whatsapp_status: '#25D366',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2'
};

export default function MarketingHub() {
  const [tab, setTab] = useState(0);
  const [segments, setSegments] = useState([]);
  const [drips, setDrips] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [postDlg, setPostDlg] = useState({ open: false, data: null });

  const load = async () => {
    setLoading(true);
    try {
      const [s, d, sp, sm] = await Promise.all([
        getSegments(),
        getDripCampaigns(),
        getSocialPosts(),
        getMarketingSummary()
      ]);
      setSegments(s.data.segments || []);
      setDrips(d.data.campaigns || []);
      setSocialPosts(sp.data.posts || []);
      setSummary(sm.data);
    } catch (e) {
      toast.error("Veri yüklenemedi: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doReclassify = async () => {
    setBusy(true);
    try {
      const r = await reclassifySegments();
      toast.success(`${r.data.users} kullanıcı analiz edildi, ${r.data.matches} eşleşme`);
      load();
    } catch (e) { toast.error("Hata"); }
    finally { setBusy(false); }
  };

  const openNewPost = () => setPostDlg({
    open: true,
    data: {
      platform: 'instagram',
      content_type: 'image',
      caption: '',
      hashtags: '',
      scheduled_at: new Date(Date.now() + 60*60*1000).toISOString().slice(0,16)
    }
  });

  const savePost = async () => {
    if (!postDlg.data.scheduled_at) { toast.error("Tarih gerekli"); return; }
    setBusy(true);
    try {
      await createSocialPost(postDlg.data);
      toast.success("Sosyal medya post'u sıraya alındı");
      setPostDlg({ open: false });
      load();
    } catch (e) { toast.error("Kayıt hatası"); }
    finally { setBusy(false); }
  };

  const doCancelPost = async (id) => {
    if (!window.confirm("Post'u iptal etmek istiyor musunuz?")) return;
    try {
      await cancelSocialPost(id);
      toast.success("İptal edildi");
      load();
    } catch (e) { toast.error("Hata"); }
  };

  if (loading) return <Box p={3}><LinearProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Toaster />

      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1565C0' }}>
            🚀 Pazarlama Merkezi
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Segment yönetimi · Drip kampanyalar · Sosyal medya scheduler
          </Typography>
        </Box>
        <IconButton onClick={load} color="primary"><RefreshIcon /></IconButton>
      </Stack>

      {/* ÖZET KARTLARI */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderTop: '3px solid #1565C0' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <GroupsIcon color="primary" />
                  <Box>
                    <Typography variant="h5" fontWeight={800}>{segments.length}</Typography>
                    <Typography variant="caption">Aktif Segment</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderTop: '3px solid #10b981' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <AutoAwesomeIcon sx={{ color: '#10b981' }} />
                  <Box>
                    <Typography variant="h5" fontWeight={800}>
                      {drips.filter(d => d.is_active).length}/{drips.length}
                    </Typography>
                    <Typography variant="caption">Aktif Drip</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderTop: '3px solid #E67E22' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <EmailIcon sx={{ color: '#E67E22' }} />
                  <Box>
                    <Typography variant="h5" fontWeight={800}>{summary.email_30d?.total_sent || 0}</Typography>
                    <Typography variant="caption">Email (30 gün)</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderTop: '3px solid #7c3aed' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <ShareIcon sx={{ color: '#7c3aed' }} />
                  <Box>
                    <Typography variant="h5" fontWeight={800}>{socialPosts.length}</Typography>
                    <Typography variant="caption">Sosyal Post</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TABS */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<GroupsIcon />} iconPosition="start" label={`Segmentler (${segments.length})`} />
          <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label={`Drip Kampanya (${drips.length})`} />
          <Tab icon={<ShareIcon />} iconPosition="start" label={`Sosyal Medya (${socialPosts.length})`} />
        </Tabs>
      </Paper>

      {/* TAB 0: Segments */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="outlined" onClick={doReclassify} disabled={busy} startIcon={<RefreshIcon />}>
              {busy ? "İşleniyor..." : "Tüm Müşterileri Yeniden Sınıfla"}
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {segments.map(s => (
              <Grid item xs={12} md={6} key={s.id}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.code} · {s.rule_type}</Typography>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography variant="h4" fontWeight={800} color="primary.main">
                          {s.member_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">üye</Typography>
                      </Stack>
                    </Stack>
                    {s.description && (
                      <Typography variant="body2" color="text.secondary">{s.description}</Typography>
                    )}
                    <Chip label={s.is_active ? 'Aktif' : 'Pasif'} size="small"
                      color={s.is_active ? 'success' : 'default'} sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* TAB 1: Drip Campaigns */}
      {tab === 1 && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kampanya</TableCell>
                  <TableCell>Tetikleyici</TableCell>
                  <TableCell>Adımlar</TableCell>
                  <TableCell>Aktif Üye</TableCell>
                  <TableCell>Tamamlanan</TableCell>
                  <TableCell>Statü</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drips.map(d => (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      <strong>{d.name}</strong>
                      {d.segment_name && <><br/><Typography variant="caption" color="text.secondary">→ {d.segment_name}</Typography></>}
                    </TableCell>
                    <TableCell><Chip label={d.trigger_type} size="small" /></TableCell>
                    <TableCell>{d.step_count}</TableCell>
                    <TableCell><Chip label={d.active_subs} color="primary" size="small" /></TableCell>
                    <TableCell><Chip label={d.completed_subs} color="success" size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip label={d.is_active ? 'Aktif' : 'Pasif'} size="small"
                        color={d.is_active ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
                {drips.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"><em>Henüz drip kampanya yok</em></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* TAB 2: Social Posts */}
      {tab === 2 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewPost}>
              Yeni Sosyal Medya Postu
            </Button>
          </Stack>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>İçerik</TableCell>
                    <TableCell>Yayın Zamanı</TableCell>
                    <TableCell>Statü</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {socialPosts.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Chip label={p.platform} size="small"
                          sx={{ bgcolor: PLATFORM_COLOR[p.platform], color: 'white' }} />
                      </TableCell>
                      <TableCell>{p.content_type}</TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.caption?.slice(0, 80) || '(boş)'}
                      </TableCell>
                      <TableCell>{new Date(p.scheduled_at).toLocaleString('tr-TR')}</TableCell>
                      <TableCell>
                        <Chip label={p.status} size="small"
                          color={p.status === 'posted' ? 'success' : p.status === 'failed' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell>
                        {p.status === 'queued' && (
                          <Button size="small" color="error" onClick={() => doCancelPost(p.id)}>İptal</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {socialPosts.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center"><em>Henüz post yok</em></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
            META credentials geldiğinde sosyal medya post'ları IG/FB'ye otomatik yayınlanır.
            Şu an placeholder mode (queued post'lar bekleyecek).
          </Alert>
        </Box>
      )}

      {/* POST EKLEME DIALOG */}
      <Dialog open={postDlg.open} onClose={() => setPostDlg({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Sosyal Medya Postu</DialogTitle>
        <DialogContent dividers>
          {postDlg.data && (
            <Stack spacing={2} mt={1}>
              <Stack direction="row" spacing={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Platform</InputLabel>
                  <Select label="Platform" value={postDlg.data.platform}
                    onChange={e => setPostDlg(d => ({ ...d, data: { ...d.data, platform: e.target.value }}))}>
                    <MenuItem value="instagram">Instagram</MenuItem>
                    <MenuItem value="facebook">Facebook</MenuItem>
                    <MenuItem value="whatsapp_status">WhatsApp Status</MenuItem>
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>İçerik Tipi</InputLabel>
                  <Select label="İçerik Tipi" value={postDlg.data.content_type}
                    onChange={e => setPostDlg(d => ({ ...d, data: { ...d.data, content_type: e.target.value }}))}>
                    <MenuItem value="image">Görsel</MenuItem>
                    <MenuItem value="carousel">Carousel</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="text">Metin</MenuItem>
                    <MenuItem value="product_spotlight">Ürün spotlight</MenuItem>
                    <MenuItem value="testimonial">Müşteri yorumu</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TextField label="Caption" fullWidth multiline minRows={4}
                value={postDlg.data.caption}
                onChange={e => setPostDlg(d => ({ ...d, data: { ...d.data, caption: e.target.value }}))} />
              <TextField label="Hashtag'ler" fullWidth placeholder="#ambalaj #toptan #miksanplastik"
                value={postDlg.data.hashtags}
                onChange={e => setPostDlg(d => ({ ...d, data: { ...d.data, hashtags: e.target.value }}))} />
              <TextField label="Yayın Zamanı" type="datetime-local" fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={postDlg.data.scheduled_at}
                onChange={e => setPostDlg(d => ({ ...d, data: { ...d.data, scheduled_at: e.target.value }}))} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDlg({ open: false })}>İptal</Button>
          <Button variant="contained" onClick={savePost} disabled={busy}>
            {busy ? "Kaydediliyor..." : "Sıraya Al"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
